<template>
  <v-navigation-drawer
    id="core-navigation-drawer"
    v-model="drawer"
    app
    width="260"
    :color="$vuetify.theme.dark ? '#0D0D0D' : '#FFFFFF'"
    class="elevment-drawer"
    mobile-breakpoint="960"
  >
    <!-- Cabeçalho (Tenant) -->
    <div class="pa-5 d-flex align-center">
      <v-avatar size="32" color="primary" class="mr-3">
        <v-img v-if="tenantFavicon" :src="tenantFavicon" />
        <span v-else class="white--text font-weight-bold">{{ tenantInitials }}</span>
      </v-avatar>
      <span class="text-subtitle-1 font-weight-bold tracking-tight text--primary">{{ tenantDisplayName }}</span>
    </div>

    <!-- Banner Modo Visualização -->
    <div v-if="isViewOnlyMode" class="view-only-banner-sidebar px-4 py-3 mb-4 mx-3">
      <div class="d-flex align-center mb-1">
        <v-icon small color="primary" class="mr-2">mdi-eye-outline</v-icon>
        <span class="text-caption font-weight-bold primary--text">{{ $t('nav.viewOnly') }}</span>
      </div>
      <div class="text-body-2 font-weight-medium text--primary">{{ managerName }}</div>
      <v-btn x-small outlined color="primary" block class="mt-2" @click="switchManager">{{ $t('nav.switchManager') }}</v-btn>
    </div>

    <!-- Itens do Menu Lateral -->
    <v-list nav class="px-3">
      <template v-for="(item, i) in computedItems">
        <v-subheader
          v-if="item.header"
          :key="`header-${i}`"
          class="text-caption font-weight-bold text--secondary mt-2 mb-1 pl-2"
        >
          {{ item.header }}
        </v-subheader>

        <v-divider
          v-else-if="item.divider"
          :key="`divider-${i}`"
          class="my-3 mx-2 theme-divider"
        />


        <v-list-item
          v-else
          :key="`item-${i}`"
          :to="item.to ? { path: item.to } : null"
          link
          active-class="elevment-active-item"
          class="mb-1 rounded-lg"
        >
          <v-list-item-icon class="mr-3">
            <v-icon size="20" class="item-icon">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="item-title">{{ item.title }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="item.badge">
            <v-chip x-small color="grey darken-2" text-color="white">{{ item.badge }}</v-chip>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>

    <!-- Rodapé: Card do Usuário -->
    <template v-slot:append>
      <div class="pa-4 drawer-append-section">
        
        <!-- Container principal alinhando os dois blocos -->
        <div class="d-flex align-center justify-space-between">
          
          <!-- Bloco do Perfil → abre menu para cima -->
          <v-menu
            top
            offset-y
            :nudge-top="8"
            origin="bottom left"
            transition="slide-y-reverse-transition"
            content-class="elevation-8 rounded-lg drawer-user-menu"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="py-2 px-2 rounded-lg d-flex align-center hover-effect flex-grow-1 mr-1"
                style="overflow: hidden; cursor: pointer;"
                v-bind="attrs"
                v-on="on"
              >
                <!-- Avatar -->
                <v-avatar size="36" color="primary" class="mr-3" style="flex-shrink: 0;">
                  <v-img v-if="userAvatarUrl" :src="userAvatarUrl" :alt="currentUser && currentUser.name" />
                  <span v-else class="white--text text-subtitle-2">{{ userInitials }}</span>
                </v-avatar>
                <!-- Nome -->
                <div class="flex-grow-1 overflow-hidden">
                  <div class="text-body-2 font-weight-bold text-truncate text--primary">
                    {{ currentUser && currentUser.name || 'Usuário' }}
                  </div>
                </div>
                <v-icon size="14" class="text--secondary ml-1" style="flex-shrink:0">mdi-chevron-up</v-icon>
              </div>
            </template>

            <v-list dense class="pa-1">
              <v-list-item @click="$router.push('/pages/user')" class="rounded-lg drawer-menu-item">
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon size="18" color="#22d3ee">mdi-account-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="text-body-2 font-weight-medium">{{ $t('nav.profile') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="$router.push('/settings/user')" class="rounded-lg drawer-menu-item">
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon size="18" color="#22d3ee">mdi-cog-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="text-body-2 font-weight-medium">{{ $t('nav.settings') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Sair -->
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="user-logout-btn"
                v-bind="attrs"
                v-on="on"
                @click="isViewOnlyMode ? exitViewOnly() : logout()"
              >
                <v-icon size="20" class="text--secondary transition-fast-in-fast-out logout-icon">
                  {{ isViewOnlyMode ? 'mdi-exit-to-app' : 'mdi-logout-variant' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isViewOnlyMode ? $t('nav.exitViewOnly') : $t('nav.exit') }}</span>
          </v-tooltip>

        </div>

      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex'
import UsersService from '@/services/users.service'

export default {
  name: 'DashboardCoreDrawer',
  data: () => ({
    tenantFavicon: null,
    tenantName: '',
    userAvatarUrl: null,
  }),

  computed: {
    ...mapState(['drawer']),

    items () {
      return [
        { header: this.$t('nav.sections.main') },
        { icon: 'mdi-view-dashboard',   title: 'Dashboard',                 to: '/'            },
        { header: this.$t('nav.sections.performance') },
        { icon: 'mdi-calendar-account', title: 'One-on-One',                to: '/one-on-ones' },
        { icon: 'mdi-notebook-edit',    title: 'PDI',                       to: '/pdis'        },
        { icon: 'mdi-bullhorn',         title: this.$t('nav.campaigns'),    to: '/campaigns'   },
        { icon: 'mdi-comment-multiple', title: 'Feedbacks',                 to: '/feedbacks'   },
        { header: this.$t('nav.sections.analytics') },
        { icon: 'mdi-file-chart',       title: this.$t('nav.reports'),      to: '/reports'     },
      ]
    },

    managerItems () {
      return [
        { icon: 'mdi-view-dashboard-outline',         title: 'Dashboard',                    to: '/'            },
        { icon: 'mdi-monitor-dashboard',              title: 'Dashboard V2',                 to: '/dashboard-v2' },
        { header: this.$t('nav.sections.people') },
        { icon: 'mdi-account-multiple-outline',       title: this.$t('nav.users'),           to: '/users'       },
        { icon: 'mdi-account-group-outline',          title: this.$t('nav.teams'),           to: '/teams'       },
        { icon: 'mdi-office-building-outline',        title: this.$t('nav.departments'),     to: '/departments' },
        { header: this.$t('nav.sections.performance') },
        { icon: 'mdi-bullseye-arrow',                 title: 'PDI',                          to: '/pdi-manager' },
        { icon: 'mdi-bullhorn-outline',               title: this.$t('nav.campaigns'),       to: '/campaigns'   },
        { icon: 'mdi-medal-outline',                  title: this.$t('nav.skills'),          to: '/skills'      },
        { header: this.$t('nav.sections.management') },
        { icon: 'mdi-calendar-account-outline',       title: 'One-on-One',                   to: '/one-on-ones' },
        { icon: 'mdi-comment-outline',                title: 'Feedbacks',                    to: '/feedbacks'   },
        { icon: 'mdi-checkbox-marked-circle-outline', title: this.$t('nav.approvals'),       to: '/approval'    },
        { header: this.$t('nav.sections.analytics') },
        { icon: 'mdi-chart-bar',                      title: this.$t('nav.reports'),         to: '/reports'     },
      ]
    },

    isViewOnlyMode () {
      return this.$store.getters.isViewOnlyMode
    },

    managerName () {
      return this.$store.getters.viewOnlyManagerName || 'Gestor'
    },

    computedItems () {
      const role = this.currentUser?.role
      if (role === 'MANAGER' || this.isViewOnlyMode) {
        return this.managerItems
      }
      return this.items
    },

    tenantDisplayName () {
      return this.tenantName || 'ElevMent'
    },
    
    tenantInitials () {
      const name = this.tenantName || 'ElevMent'
      const words = name.trim().split(/\s+/)
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    
    currentUser () {
      try {
        const userStr = localStorage.getItem('user')
        return userStr ? JSON.parse(userStr) : null
      } catch (error) {
        return null
      }
    },

    userInitials () {
      const name = (this.currentUser && this.currentUser.name) || ''
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase() || '??'
    },
  },

  created () {
    this.loadTenantInfo()
  },

  methods: {
    async loadTenantInfo () {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          if (user.tenantName) this.tenantName = user.tenantName
          if (user.tenantFavicon) this.tenantFavicon = user.tenantFavicon
          if (user.avatarUrl) this.userAvatarUrl = user.avatarUrl
        }
        const response = await UsersService.getProfile()
        const profile = response.data
        if (profile.tenantName) this.tenantName = profile.tenantName
        if (profile.tenantFavicon) this.tenantFavicon = profile.tenantFavicon
        if (profile.avatarUrl) this.userAvatarUrl = profile.avatarUrl
      } catch (error) {
        console.error('Erro ao carregar info do tenant:', error)
      }
    },

    onSoonClick () {
      this.$store.dispatch('showSnackbar', {
        color: 'info',
        text: 'Funcionalidade em breve — disponível em uma próxima atualização.',
      })
    },

    switchManager () {
      this.$router.push('/tenant-admin')
    },

    exitViewOnly() {
      this.$store.dispatch('clearViewOnlyMode')
      this.$router.push('/tenant-admin')
    },

    logout() {
      localStorage.removeItem('firebaseToken')
      localStorage.removeItem('user')
      localStorage.removeItem('tenantId')
      localStorage.removeItem('backendToken')
      this.$store.dispatch('clearViewOnlyMode')
      this.$store.dispatch('clearUserConfig')
      this.$router.push('/login')
    },
  }
}
</script>

<style scoped>
/* Bordas Responsivas ao Tema */
.theme--dark.elevment-drawer {
  border-right: 1px solid rgba(255, 255, 255, 0.05) !important;
  background-color: #0D0D0D !important;
}

#core-navigation-drawer.theme--dark,
#core-navigation-drawer.theme--dark .v-navigation-drawer__content {
  background-color: #0D0D0D !important;
}

#core-navigation-drawer.theme--dark .v-sheet,
#core-navigation-drawer.theme--dark .v-list,
#core-navigation-drawer.theme--dark .v-navigation-drawer__append {
  background-color: #0D0D0D !important;
}

.theme--light.elevment-drawer { border-right: 1px solid rgba(0, 0, 0, 0.05) !important; }

.drawer-append-section {
  border-top: 1px solid rgba(128, 128, 128, 0.25);
}

.theme-divider {
  border-color: rgba(128, 128, 128, 0.2) !important;
}

/* Estado Inativo dos Itens */
.theme--dark .item-icon, .theme--dark .item-title { color: #AEAEB2 !important; }
.theme--light .item-icon, .theme--light .item-title { color: #86868B !important; }

/* Item Ativo (Ciano) */
.elevment-active-item {
  background-color: rgba(0, 229, 255, 0.08) !important;
  position: relative;
}

.theme--dark .elevment-active-item { background-color: #1A1A1A !important; }

.elevment-active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 15%;
  height: 70%;
  width: 3px;
  background-color: #00E5FF;
  border-radius: 0 4px 4px 0;
}

.elevment-active-item .item-title, 
.elevment-active-item .item-icon {
  color: #00E5FF !important;
  font-weight: 600 !important;
}

/* Hover Effect do Perfil */
.hover-effect { cursor: pointer; transition: background-color 0.2s ease; }
.theme--dark .hover-effect:hover { background-color: rgba(255, 255, 255, 0.05); }
.theme--light .hover-effect:hover { background-color: rgba(0, 0, 0, 0.04); }

/* Hover Effect Exclusivo do Botão Sair (Vermelho) */
.user-logout-btn { transition: all 0.2s ease; }
.theme--dark .user-logout-btn:hover { background-color: rgba(239, 68, 68, 0.15) !important; }
.theme--light .user-logout-btn:hover { background-color: rgba(239, 68, 68, 0.1) !important; }

.user-logout-btn:hover .logout-icon { color: #EF4444 !important; }

/* Itens "Em breve" (bloqueados) */
.drawer-soon-item { cursor: default !important; opacity: 0.55; }
.drawer-soon-item:hover { background-color: transparent !important; }
.item-icon--soon, .item-title--soon { opacity: 0.85; }

.soon-badge {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 5px;
  white-space: nowrap;
}
.theme--dark .soon-badge { background-color: #1f1f1f; color: #777777; border: 1px solid #2a2a2a; }
.theme--light .soon-badge { background-color: #ECECEC; color: #999999; border: 1px solid #E0E0E0; }

/* Menu popup do usuário */
.drawer-menu-item { transition: background-color 0.15s ease; }
.theme--dark .drawer-menu-item:hover { background-color: rgba(255,255,255,0.06) !important; }
.theme--light .drawer-menu-item:hover { background-color: rgba(0,0,0,0.04) !important; }

/* Banner View Only */
.view-only-banner-sidebar {
  background-color: rgba(0, 229, 255, 0.05);
  border-radius: 8px;
  border-left: 3px solid #00E5FF;
}
</style>

<style>
/* Menu popup — teleportado fora do scoped, precisa ser global */
.theme--dark .drawer-user-menu {
  background-color: #1a1a1a !important;
  border: 1px solid rgba(255,255,255,0.07) !important;
  min-width: 200px;
}
.theme--light .drawer-user-menu {
  background-color: #ffffff !important;
  border: 1px solid rgba(0,0,0,0.07) !important;
  min-width: 200px;
}
</style>